import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'formik';
import { useDebounce } from '../../shared/effects';
import { evaluateQuestionConditions } from '../../shared/conditions';
import { getQuestionsTotal, getQuestionsCompleted } from '../../shared/lib';
import {
  UPDATE_FORM_PROGRESS,
  UPDATE_FORM_QUESTIONS,
} from '../../shared/reducer';
import { useStateValue } from '../../shared/state';
import styles from './styles';
import { camelize } from 'humps';
import { useTranslation } from 'react-i18next';
import { showEmployeeDashboard } from 'employee-pro-shared/constants';

const dispatchUpdateFormProgress = ({
  dispatch,
  required,
  hidden,
  conditions,
  values,
  initialValues,
  isNew,
  dirty,
}) => {
  const questionsTotal = getQuestionsTotal({ required, hidden });
  const questionsCompleted = getQuestionsCompleted({
    required,
    hidden,
    initialValues,
    values,
    questionsTotal,
    isNew,
    dirty,
  });
  questionsTotal > 0 &&
    dispatch({
      type: UPDATE_FORM_PROGRESS,
      questionsTotal,
      questionsCompleted,
    });
};

const dispatchUpdateFormQuestions = ({
  dispatch,
  required,
  hidden,
  conditions,
  values,
  initialValues,
  isNew,
  dirty,
}) => {
  dispatch({
    type: UPDATE_FORM_QUESTIONS,
    ...evaluateQuestionConditions({ values, conditions }),
  });
};

const FormControls = ({
  isNew,
  leftOffset,
  submission,
  formik: { initialValues, dirty, isSubmitting, values, isValid, submitCount },
}) => {
  const [
    {
      questionsTotal,
      questionsCompleted,
      required,
      hidden,
      conditions,
      features,
    },
    dispatch,
  ] = useStateValue();
  const wasSubmitted = submitCount > 0;
  const inProgress = !isSubmitting && dirty;
  const showUnsavedChanges =
    inProgress && (!wasSubmitted || (wasSubmitted && isValid));
  const showYouHaveErrors = inProgress && wasSubmitted && !isValid;

  const effectArgs = {
    dispatch,
    required,
    hidden,
    conditions,
    values,
    initialValues,
    isNew,
    dirty,
  };

  // Update on First Render
  useEffect(() => {
    dispatchUpdateFormQuestions(effectArgs);
    dispatchUpdateFormProgress(effectArgs);
  }, []);
  // Update on Value changes
  useEffect(() => {
    if (!isSubmitting) {
      dispatchUpdateFormProgress(effectArgs);
      dispatchUpdateFormQuestions(effectArgs);
    }
    return () => {
      if (!isSubmitting) {
        // Update one last time in clean up call back
        // Just in case the debounce swallowed some input
        dispatchUpdateFormProgress(effectArgs);
        dispatchUpdateFormQuestions(effectArgs);
      }
    };
  }, [useDebounce(values, 300)]);

  const { t } = useTranslation();

  return (
    <div
      className={cx(
        styles.controls,
        styles[camelize(`${submission.name}Controls`)],
        {
          [styles.employeeDashboardControls]: showEmployeeDashboard,
          [styles.courseSurvey]: submission.name.includes('course_survey'),
          [styles.full]: leftOffset === '364px',
          [styles.collapsed]: leftOffset === '24px',
        }
      )}
      style={{ left: leftOffset }}
    >
      {/* It might be nice to display a progress bar */}
      {features.progressMeter && questionsTotal > 0 && (
        <div
          className={cx(styles.progress, {
            [styles.showProgressMeter]: !isSubmitting,
          })}
          aria-label={`${t('Completed')} ${questionsCompleted} ${t(
            'of'
          )} ${questionsTotal} ${t('required questions')}`}
        >
          <progress max={questionsTotal} value={questionsCompleted}></progress>
        </div>
      )}
      <div
        className={cx(styles.unsavedChanges, {
          [styles.showUnsavedChanges]: showUnsavedChanges,
          [styles.showUnsavedChangesWithErrors]: showYouHaveErrors,
        })}
      >
        {showUnsavedChanges && t('You have unsubmitted changes')}
        {showYouHaveErrors &&
          t('There are errors, please review your submission')}
      </div>
      <button className={styles.button} type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('Submitting...') : t('Submit')}
      </button>
    </div>
  );
};

FormControls.propTypes = {
  isNew: PropTypes.bool.isRequired,
  formik: PropTypes.object.isRequired,
  submission: PropTypes.object.isRequired,
};

export default connect(FormControls);
